import React, { useEffect, useState } from 'react';
import { ShieldAlert, Ban, AlertCircle } from 'lucide-react';
import { api } from '../../services/authenticatedApi';
import type { PolicyViolation } from '../../services/policyService';

export const PolicyViolationsPanel: React.FC<{ repositoryId: string }> = ({ repositoryId }) => {
  const [violations, setViolations] = useState<PolicyViolation[] | null>(null);

  useEffect(() => {
    api
      .get<{ violations: PolicyViolation[] }>(`/repositories/${repositoryId}/policy-violations`)
      .then((res) => setViolations(res.data.violations))
      .catch(() => setViolations(null));
  }, [repositoryId]);

  // Only render when this repo actually breaks a policy.
  if (!violations || violations.length === 0) return null;

  return (
    <section className="mt-6 p-5 rounded-2xl border border-amber-200/70 dark:border-amber-900/60 bg-amber-50/50 dark:bg-amber-950/20 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <ShieldAlert className="w-5 h-5 text-amber-500" />
        <h2 className="font-semibold text-gray-900 dark:text-white">Policy violations</h2>
        <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
          {violations.length}
        </span>
      </div>
      <ul className="space-y-2">
        {violations.map((v) => (
          <li key={v.entitySlug} className="flex items-start gap-2 text-sm">
            {v.kind === 'prohibited_in_use' ? (
              <Ban className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
            )}
            <div>
              <span className="text-gray-900 dark:text-gray-200">
                {v.kind === 'prohibited_in_use'
                  ? `${v.entityName} is prohibited by fleet policy but in use here`
                  : `${v.entityName} is required by fleet policy but missing`}
              </span>
              {v.note && (
                <span className="block text-xs text-gray-500 dark:text-gray-400">{v.note}</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};
